import { useEffect, useState } from 'react';
import Modal from './Modal';
import { apiFetch } from '../api/client';
import { encodePathSegment } from '../lib/url';
import { REVOCATION_REASONS } from '../lib/revocationReasons';

export interface SchemaStatusTarget {
  id: string;
  name: string;
  deactivated?: boolean;
  credentialCount?: number;
}

/**
 * Confirm deactivating or reactivating a schema. Deactivating only blocks new
 * issuance; the issuer can opt in to also revoking the credentials they issued
 * under it — a permanent step, so it is off by default and needs a reason.
 */
export default function SchemaStatusDialog({
  target,
  onClose,
  onDone,
}: {
  target: SchemaStatusTarget | null;
  onClose: () => void;
  onDone: (message?: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [revokeIssued, setRevokeIssued] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    setError('');
    setRevokeIssued(false);
    setReason('');
  }, [target?.id]);

  if (!target) return null;
  const deactivating = !target.deactivated;
  const count = target.credentialCount ?? 0;

  const apply = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch<{ revokedCount?: number }>(
        `/credentials/schemas/${encodePathSegment(target.id)}/status`,
        {
          method: 'PUT',
          body: JSON.stringify(
            deactivating && revokeIssued
              ? { deactivated: true, revokeIssued: true, reason }
              : { deactivated: deactivating },
          ),
        },
      );
      onDone(
        deactivating && revokeIssued
          ? `${target.name} deactivated and ${res?.revokedCount ?? 0} credential(s) revoked.`
          : undefined,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update schema status');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => { if (!busy) onClose(); }}
      title={deactivating ? 'Deactivate schema' : 'Reactivate schema'}
    >
      <div className="space-y-4">
        <p className="text-sm text-text-main">
          {deactivating ? (
            <>Deactivate <span className="font-bold">{target.name}</span>?</>
          ) : (
            <>Reactivate <span className="font-bold">{target.name}</span> so new credentials can be issued against it again?</>
          )}
        </p>

        {deactivating && (
          <>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <p className="font-bold">No new credentials can be issued against this schema.</p>
              <p className="mt-1">
                The {count.toLocaleString()} credential(s) already issued stay valid unless you also revoke them below.
                Deactivation can be undone later; revocation cannot.
              </p>
            </div>

            {count > 0 && (
              <div className="rounded-xl border border-border-subtle p-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 accent-red-600"
                    checked={revokeIssued}
                    onChange={(e) => setRevokeIssued(e.target.checked)}
                  />
                  <span className="text-sm text-text-main">
                    <span className="font-bold">Also revoke the active credentials issued under this schema</span>
                    <span className="mt-0.5 block text-xs text-text-muted">
                      Revokes the ones issued by this schema's issuer. Each holder sees the reason, and verifiers will
                      reject them. This is permanent, even if the schema is reactivated.
                    </span>
                  </span>
                </label>
                {revokeIssued && (
                  <div className="mt-3">
                    <label className="mb-1 block text-xs font-bold text-text-muted">Revocation reason</label>
                    <select
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-full rounded-lg border border-border-stroke bg-white px-3 py-2 text-sm text-text-main"
                    >
                      <option value="">Select reason…</option>
                      {Object.entries(REVOCATION_REASONS).map(([code, label]) => (
                        <option key={code} value={code}>{code} - {label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="flex justify-end gap-3">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            onClick={apply}
            disabled={busy || (revokeIssued && !reason)}
            className={`btn-primary ${
              !deactivating ? '' : revokeIssued ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            {busy
              ? 'Saving…'
              : !deactivating
                ? 'Reactivate'
                : revokeIssued
                  ? 'Deactivate & revoke'
                  : 'Deactivate'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
